import { useEffect } from 'react';

import { useSearchParams } from 'react-router';

import { useAppDispatch, useAppSelector } from 'dux/app/hooks';

import { productsCategories } from 'constants/products';
import {
  HAIRCARE_QUESTION_LENGTH,
  HAIRCARE_QUESTION_TEXTURE,
  HAIRCARE_QUESTION_TYPE,
  SKINCARE_QUESTION_INDOOR_EXPOSURE,
  SKINCARE_QUESTION_OUTDOOR_EXPOSURE,
  SKINCARE_QUESTION_SPORTS,
} from '../constants';

import * as trackingActions from 'dux/tracking/actions';
import { getIsAuthenticated } from 'dux/auth/selectors';
import * as consultationSelectors from 'dux/consultation/selectors';
import {
  getHasCompletedHaircareConsultation,
  getHasCompletedSkincareConsultation,
} from 'dux/user/selectors';
import { initializeConsultation } from 'dux/consultation/thunks';

import { useConsultationCategory } from './useConsultationCategory';
import { useConsultationSubsetCategory } from './useConsultationSubsetCategory';

/**
 * Mapping for questions indexes
 * With transitions screens, real questions don't have the right index
 */
const questionsIndexesToBeTrackedOnClick = {
  // Haircare
  [HAIRCARE_QUESTION_TEXTURE]: 2,
  [HAIRCARE_QUESTION_LENGTH]: 3,
  [HAIRCARE_QUESTION_TYPE]: 4,
  // Skincare
  [SKINCARE_QUESTION_OUTDOOR_EXPOSURE]: 2,
  [SKINCARE_QUESTION_SPORTS]: 3,
  [SKINCARE_QUESTION_INDOOR_EXPOSURE]: 4,
};

const useConsultationTracking = ({ questionIndex, questionName }) => {
  const consultationCategory = useConsultationCategory();
  const consultationSubsetCategory = useConsultationSubsetCategory();
  const [searchParams] = useSearchParams();
  const dataFrom = searchParams.get('data-from');

  const dispatch = useAppDispatch();
  const hasCompletedHaircareConsultation = useAppSelector(getHasCompletedHaircareConsultation);
  const hasCompletedSkincareConsultation = useAppSelector(getHasCompletedSkincareConsultation);
  const isAuthenticated = useAppSelector(getIsAuthenticated);
  const shouldInitializeConsultation = useAppSelector(
    consultationSelectors.getShouldInitializeConsultation
  );
  const shouldTrackConsultationStart = useAppSelector(state =>
    consultationSelectors.shouldTrackConsultationStart(state, {
      consultationCategory,
      consultationSubsetCategory,
      questionIndex,
      routeParams: searchParams,
    })
  );

  useEffect(() => {
    if (shouldInitializeConsultation) {
      dispatch(initializeConsultation({ dataFrom }));
      dispatch(
        trackingActions.trackIterableEvent('Started Consultation', {
          category: consultationCategory,
        })
      );
    }
  }, [shouldInitializeConsultation, dataFrom, consultationCategory]);

  useEffect(() => {
    if (shouldTrackConsultationStart) {
      dispatch(trackingActions.trackConsultationStart({ category: consultationCategory }));
    }
  }, [shouldTrackConsultationStart]);

  useEffect(() => {
    if (!shouldInitializeConsultation && isAuthenticated && shouldTrackConsultationStart) {
      dispatch(
        trackingActions.trackIterableEvent('Started Consultation', {
          category: consultationCategory,
        })
      );
    }
  }, [
    consultationCategory,
    isAuthenticated,
    shouldInitializeConsultation,
    shouldTrackConsultationStart,
  ]);

  useEffect(() => {
    if (
      questionsIndexesToBeTrackedOnClick[questionName] &&
      ((consultationCategory === productsCategories.HAIRCARE &&
        !hasCompletedHaircareConsultation) ||
        (consultationCategory === productsCategories.SKINCARE && !hasCompletedSkincareConsultation))
    ) {
      dispatch(
        trackingActions.trackConsultationQuestionReached({
          category: consultationCategory,
          questionIndex: questionsIndexesToBeTrackedOnClick[questionName],
        })
      );
    }
  }, [consultationCategory, questionIndex]);
};

export default useConsultationTracking;
